import { Loading } from '@perch-labs/shrike';
import { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { SpaceProps } from 'styled-system';
import { Container } from '~/components/layout';
import { OnChangeMeta } from '~/components/validating-input';
import { StyledInput } from '~/components/validating-input/validating-input.style';
import { convertAddressTypeToString } from '~/screens/billing-address';
import { AddressType } from '~/typings/context';
import { IS_TEST } from '~/utils/constants';
import { getStyledSystemProps } from '~/utils/styled-system';
import { copy } from './copy';
import { matchGooglePlace } from './match-google-place';
import { ParsedAddressType, parseAddress } from './parse-address';
import { useAddressValidation } from './use-address-validation';

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const LoaderContainer = styled.div`
  position: absolute;
  right: 12px;
  top: 70%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 1;
`;

const addressMock = {
  street: '401 South 2nd Street',
  state: 'IL',
  city: 'Springfield',
  zipCode: '62701',
};

type Props = {
  label?: string;
  onChange: (address: ParsedAddressType | undefined, meta: OnChangeMeta) => void;
  state?: {
    abbreviation: string;
    id: number;
    name: string;
  };
  utilityId?: number;
  utilityAccountId?: number;
  // When true with utilityAccountId, LMI eligibility is checked against the typed
  // address immediately on Google Places selection, before the address is saved.
  shouldValidateLMIAddress?: boolean;
  placeholder?: string;
  value?: ParsedAddressType | AddressType | string;
  errorText?: string;
  required?: boolean;
} & SpaceProps;

export const Address = ({
  label = 'Address',
  onChange,
  state,
  utilityId,
  utilityAccountId,
  shouldValidateLMIAddress,
  value = '',
  errorText = '',
  required,
  ...props
}: Props) => {
  const consolidatedAddress = convertAddressTypeToString(value) || '';
  const [displayValue, setDisplayValue] = useState(consolidatedAddress);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastValidatedRef = useRef<string | null>(null);
  const lastRequestedKeyRef = useRef<string | null>(null);
  const validationGenerationRef = useRef(0);
  const handlePlacesChangedRef = useRef<
    ((parsedAddress: ParsedAddressType, formattedAddress: string) => void) | null
  >(null);
  const [localErrorText, setLocalErrorText] = useState(errorText || undefined);
  const [isGooglePlacesChanged, setIsGooglePlacesChanged] = useState(false);
  const [isValidatingUtility, setIsValidatingUtility] = useState(false);
  const { validateAddress } = useAddressValidation({
    state,
    utilityId,
    utilityAccountId,
    shouldValidateLMIAddress,
  });

  const runValidation = useCallback(
    (addressToValidate: ParsedAddressType | AddressType) => {
      const generation = ++validationGenerationRef.current;
      setIsValidatingUtility(true);
      validateAddress(addressToValidate).then(validationResult => {
        if (generation !== validationGenerationRef.current) return;
        if (!validationResult.isValid) {
          setLocalErrorText(validationResult.error);
          onChange(addressToValidate, { isValid: false, name: 'address' });
        } else {
          setLocalErrorText('');
          onChange(addressToValidate, { isValid: true, name: 'address' });
        }
        setIsValidatingUtility(false);
        setDisplayValue(convertAddressTypeToString(addressToValidate) || '');
      });
    },
    [validateAddress, onChange],
  );

  const loadMap = useCallback(async () => {
    if (IS_TEST) {
      return;
    }

    // ref: https://developers.google.com/maps/documentation/javascript/load-maps-js-api
    const { Autocomplete } = (await google.maps.importLibrary(
      'places',
    )) as google.maps.PlacesLibrary;
    if (!inputRef.current) return;
    const autoCompleteService = new Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'us' },
      fields: ['address_components', 'formatted_address'],
      types: ['address'],
    });

    autoCompleteService.addListener('place_changed', () => {
      const response = autoCompleteService.getPlace();
      if (response.address_components) {
        const parsed = parseAddress(response.address_components);
        const formatted = response.formatted_address || '';
        handlePlacesChangedRef.current?.(parsed, formatted);
      }
    });
  }, [inputRef]);

  useEffect(() => {
    loadMap();
  }, [loadMap]);

  const handlePlacesChanged = useCallback(
    (parsedAddress: ParsedAddressType, formattedAddress: string) => {
      setDisplayValue(formattedAddress);
      setLocalErrorText('');
      setIsGooglePlacesChanged(true);
      lastValidatedRef.current = convertAddressTypeToString(parsedAddress) || '';
      runValidation(parsedAddress);
    },
    [runValidation],
  );

  useEffect(() => {
    handlePlacesChangedRef.current = handlePlacesChanged;
  }, [handlePlacesChanged]);

  const externalAddressKey =
    typeof value === 'object' && value ? convertAddressTypeToString(value) : '';

  useEffect(() => {
    if (!externalAddressKey) return;
    if (typeof value !== 'object' || !value) return;
    if (lastValidatedRef.current === externalAddressKey) return;
    if (lastRequestedKeyRef.current === externalAddressKey) return;

    lastRequestedKeyRef.current = externalAddressKey;
    lastValidatedRef.current = externalAddressKey;
    setDisplayValue(externalAddressKey);
    // Prop-supplied addresses (bill extraction, resumed funnels) still need a
    // Google Places match before they are treated as selected.
    setIsGooglePlacesChanged(false);
    const generation = ++validationGenerationRef.current;
    setIsValidatingUtility(true);

    matchGooglePlace(externalAddressKey, value).then(match => {
      if (generation !== validationGenerationRef.current) return;
      if (!match) {
        setLocalErrorText(copy.errors.selectSuggestedAddress);
        setIsGooglePlacesChanged(false);
        setIsValidatingUtility(false);
        onChange(value, { isValid: false, name: 'address' });
        return;
      }
      handlePlacesChanged(match.parsed, match.formattedAddress);
    });
  }, [externalAddressKey, handlePlacesChanged, onChange, value]);

  useEffect(() => {
    if (IS_TEST) return;
    if (externalAddressKey) return;
    onChange(undefined, { isValid: false, name: 'address' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!IS_TEST) return;
    if (externalAddressKey) return;

    // The mock is echoed back as `value`, so mark it validated to keep the effect
    // from re-running against a state that doesn't match the utility under test.
    lastValidatedRef.current = convertAddressTypeToString(addressMock) || '';
    lastRequestedKeyRef.current = lastValidatedRef.current;
    setDisplayValue(convertAddressTypeToString(value) || '');
    setIsGooglePlacesChanged(true);
    onChange(addressMock, { isValid: true, name: 'address' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.google, externalAddressKey]);

  const containerProps = getStyledSystemProps(props);

  return (
    <Container {...containerProps}>
      <InputContainer>
        <StyledInput
          errorText={localErrorText}
          required={required}
          label={label}
          name="address"
          ref={inputRef}
          onBlur={() => {
            if (displayValue === '') {
              setLocalErrorText('');
              return;
            }
            if (!isGooglePlacesChanged) {
              setLocalErrorText(copy.errors.selectSuggestedAddress);
            }
          }}
          onChange={e => {
            if (!IS_TEST && isGooglePlacesChanged) {
              setIsGooglePlacesChanged(false);
            }

            if (e === '') {
              setLocalErrorText('');
              lastValidatedRef.current = null;
              lastRequestedKeyRef.current = null;
              validationGenerationRef.current += 1;
              setIsValidatingUtility(false);
              onChange(undefined, { isValid: false, name: 'address' });
            }
            setDisplayValue(e);
          }}
          value={displayValue}
          placeholder={props.placeholder || ''}
          style={{
            paddingRight: isValidatingUtility ? '40px' : '12px',
            transition: 'padding-right 0.2s ease',
          }}
        />

        {!!isValidatingUtility && (
          <LoaderContainer>
            <Loading />
          </LoaderContainer>
        )}
      </InputContainer>
    </Container>
  );
};

export type { GoogleAddressType, ParsedAddressType } from './parse-address';
export { parseAddress } from './parse-address';
